import { WHITE, type Color } from '../primitives/color.js'
import { timestamp, type ClockState, type TimeControl, type Timestamp } from './types.js'

export const initialClock = (control: TimeControl): ClockState => ({
  whiteMs: control.kind === 'unlimited' ? Number.POSITIVE_INFINITY : control.initialMs,
  blackMs: control.kind === 'unlimited' ? Number.POSITIVE_INFINITY : control.initialMs,
  turnStartedAt: null,
})

const storedMs = (clock: ClockState, color: Color): number =>
  color === WHITE ? clock.whiteMs : clock.blackMs

const withMs = (clock: ClockState, color: Color, ms: number): ClockState =>
  color === WHITE ? { ...clock, whiteMs: ms } : { ...clock, blackMs: ms }

/**
 * What `color` has left at instant `now`.
 *
 * Only the side to move is losing time, and only once the clock has started, so
 * every other case is just the stored value.
 */
export const remainingMs = (
  clock: ClockState,
  turn: Color,
  color: Color,
  now: Timestamp,
): number => {
  const stored = storedMs(clock, color)
  if (clock.turnStartedAt === null || color !== turn || !Number.isFinite(stored)) return stored
  return Math.max(0, stored - (now - clock.turnStartedAt))
}

export const startClock = (clock: ClockState, now: Timestamp): ClockState => ({
  ...clock,
  turnStartedAt: now,
})

export const stopClock = (clock: ClockState, turn: Color, now: Timestamp): ClockState => {
  if (clock.turnStartedAt === null) return clock
  return { ...withMs(clock, turn, remainingMs(clock, turn, turn, now)), turnStartedAt: null }
}

/**
 * Charge the mover for their thinking time, credit the increment, and hand the
 * clock to the opponent.
 */
export const applyMoveToClock = (
  clock: ClockState,
  control: TimeControl,
  mover: Color,
  now: Timestamp,
): ClockState => {
  if (control.kind === 'unlimited' || clock.turnStartedAt === null) return clock
  const left = remainingMs(clock, mover, mover, now)
  return { ...withMs(clock, mover, left + control.incrementMs), turnStartedAt: now }
}

/**
 * The instant the side to move will run out, or null if no clock is running.
 * The Durable Object sets a single alarm for exactly this moment, so a game
 * ends on time even with nobody connected to notice.
 */
export const flagFallAt = (clock: ClockState, turn: Color): Timestamp | null => {
  if (clock.turnStartedAt === null) return null
  const stored = storedMs(clock, turn)
  return Number.isFinite(stored) ? timestamp(clock.turnStartedAt + stored) : null
}

export const hasFlagged = (clock: ClockState, turn: Color, now: Timestamp): boolean => {
  const fall = flagFallAt(clock, turn)
  return fall !== null && now >= fall
}
